import { Injectable } from "@angular/core";
import { LeaderGenerator } from "../../lib/leaderGenerator";
import { PointsCalc } from "../../lib/points/PointsCalc";
import { PointAssigmentTracked, PointsUtilities } from "../../lib/points/PointsUtilities";
import { PayoutConfig } from "../../model/payoutConfig";
import { PointStats } from "../../model/point-stats";
import { Rodeo } from "../../model/rodeo";
import { RodeoEvent } from "../../model/rodeoEvent";
import { RodeoEventRun } from "../../model/rodeoEventRun";
import { groupBy } from "../../lib/helpers";
import { PointAssignment } from "../../model/point-assignment";
import { PointsMonitoringService } from "./points.monitoring.service";
import { RodeoPointSeriesNominationsDto } from "../../lib/types/RodeoPointSeriesNominationsDto";

export class PointStatResult extends PointStats {
    result: number
}

@Injectable()
export class PointsDomainService {
    constructor(private pointsMonitoringService: PointsMonitoringService) { }

    verifyPointStatsConfiguration(
        rodeo: Rodeo,
        rodeoEvent: RodeoEvent,
        payoutConfigs: PayoutConfig[]
    ) {
        this.pointsMonitoringService.logVerifyPointsStatus(rodeo, rodeoEvent, payoutConfigs);
        const entryCost = rodeoEvent.entryCost;
        const payoutConfigsWithPointSeries = payoutConfigs.filter(
            pa => pa.pointSeries.length > 0);

        if (payoutConfigsWithPointSeries.length === 0) {
            const result = {
                validPayoutConfig: payoutConfigsWithPointSeries,
                error: 'No payout have point series assigned',
                entryCost: entryCost
            }
            this.pointsMonitoringService.logVerifyPointsStatusResult(rodeo, rodeoEvent, payoutConfigs, result);
            return result;
        }

        const payoutConfigsWithPointStats = payoutConfigsWithPointSeries.map(payout => {
            const entryCount = this.getPayoutEntryCount(payout, rodeo)
            const isSidePot = this.checkIfIsSidepot(payout);

            const firstPointAssigmentsInEachSeries: Array<PointAssignment> = this.getFirstPointAssigmentInEachSeries(payout, entryCost, isSidePot);

            const pointAssigments = this.getTrackedPointAssigments(firstPointAssigmentsInEachSeries, entryCount, payout);
            return {
                ...payout,
                trackedPointAssigments: pointAssigments,
                entryCount,
                isSidePot,
            };
        });

        if (payoutConfigsWithPointStats.filter(pc => pc.trackedPointAssigments.length > 0).length === 0) {
            const result = {
                error: 'No valid point assigments found for any point series',
                entryCost: entryCost,
                validPayoutConfig: payoutConfigsWithPointStats,
            }
            this.pointsMonitoringService.logVerifyPointsStatusResult(rodeo, rodeoEvent, payoutConfigs, result);
            return result;
        }

        const result = {
            error: '',
            validPayoutConfig: payoutConfigsWithPointStats,
            entryCost: entryCost
        };
        this.pointsMonitoringService.logVerifyPointsStatusResult(rodeo, rodeoEvent, payoutConfigs, result);
        return result;

    }

    generatePointStats(
        rodeo: Rodeo, rodeoEvent: RodeoEvent,
        payoutConfigs: PayoutConfig[], useScoreValue: boolean = true,
        rodeoPointSeriesNominations: RodeoPointSeriesNominationsDto[]
    ): PointStatResult[] {
        this.pointsMonitoringService.logPointStatsGeneratingStart(rodeo, rodeoEvent, payoutConfigs, useScoreValue, rodeoPointSeriesNominations);
        const entryCost = rodeoEvent.entryCost;
        const pointStats: PointStatResult[] = [];
        payoutConfigs.forEach(payout => {
            const entryCount = this.getPayoutEntryCount(payout, rodeo)
            const isSidePot = this.checkIfIsSidepot(payout);
            if (!payout.pointSeries || payout.pointSeries.length == 0) return;

            const firstPointAssigmentsInEachSeries: Array<PointAssignment> = this.getFirstPointAssigmentInEachSeries(payout, entryCost, isSidePot);

            const pointAssigments = this.getTrackedPointAssigments(firstPointAssigmentsInEachSeries, entryCount, payout);

            // hardcoded because of only members in point series, 
            // points max places is tracked by the point assigment places 
            // PointsCalc.CalculatePlaces(entryCount, pa.entryScaleMultiple, pa.placesAwardedPerEntryScale)
            // we just want all leaders and based on points places we will stop generating points `if (place > pa.places) return`;
            // or move to next if not member
            const maxPlaces = 100
            pointAssigments.forEach(pa => {
                const leaders = LeaderGenerator.instance().getLeaders(
                    rodeo, payout, maxPlaces, payout.includePenalties,
                    rodeoEvent.fallBackToFill, rodeoEvent.fallBackToPlace);
                leaders.forEach((l) => {
                    pa.currentPoints = PointsCalc.CalculatePoints(pa.pointsBase, entryCount, pa.entryScaleMultiple, pa.pointsAwardedPerEntryScale);
                    let place = 0;
                    l.runs.forEach((r) => {
                        if (place === 0)
                            place = l.startPlace

                        if (place > pa.places) return;

                        if (pa.currentPoints <= 0) return;

                        if (r.noTime) return;

                        // Membership Validation
                        const constestantHasActiveMembership = r.contestantOne.memberships
                            .find(m => !m.isExpired && !m.permit && m.membership.organizationId === rodeo.organization.id);

                        let contestantOnePassMembershipValidation = !pa.membersOnly || (constestantHasActiveMembership && pa.membersOnly)

                        const hasContestantTwo = !!r.contestantTwo;
                        const constestantTwoHasActiveMembership = hasContestantTwo ? r.contestantTwo.memberships
                            .find(m => !m.isExpired && !m.permit && m.membership.organizationId === rodeo.organization.id) : null;

                        let contestantTwoPassMembershipValidation = (!pa.membersOnly && hasContestantTwo) || (constestantTwoHasActiveMembership && pa.membersOnly);

                        // Nomination Validation
                        let contestantOnePassNominationValidation = true;
                        let contestantTwoPassNominationValidation = true;
                        if (pa.enableNominations) {
                            // NOTE: Here  we might need to add the check if contestant has not exceded their max nominations for this point series
                            // for now we trust that validation for the entry flow
                            contestantOnePassNominationValidation = !!rodeoPointSeriesNominations.find(rn => rn.contestantId === r.contestantOneId);
                            if (hasContestantTwo)
                                contestantTwoPassNominationValidation = !!rodeoPointSeriesNominations.find(rn => rn.contestantId === r.contestantTwoId);
                            else
                                contestantTwoPassNominationValidation = false;
                        }

                        // Payout contestant one | two validation
                        let contestantOnePassPayoutValidation = payout.contestantOnePayout && !r.isExtraRunForContestantOne
                        let contestantTwoPassPayoutValidation = payout.contestantTwoPayout && !r.isExtraRunForContestantTwo


                        const shouldContestantOneGetPoints = contestantOnePassMembershipValidation && contestantOnePassNominationValidation && contestantOnePassPayoutValidation;
                        const shouldContestantTwoGetPoints = contestantTwoPassMembershipValidation && contestantTwoPassNominationValidation && contestantTwoPassPayoutValidation;

                        if (!shouldContestantOneGetPoints && !shouldContestantTwoGetPoints) {
                            return;
                        }
                        const newPointStat = PointsUtilities.handlePointStatAssigment(
                            pa, place, r.id, payout.id, l.name, shouldContestantOneGetPoints, shouldContestantTwoGetPoints);
                        const result = this.getResult(r, useScoreValue, payout.isAggregate || payout.isAverage)
                        pointStats.push(Object.assign(newPointStat, { result }));
                        place = place + 1;
                    });
                });
            });
        });
        this.pointsMonitoringService.logGeneratedPointStats(rodeo, rodeoEvent, payoutConfigs, useScoreValue, pointStats, rodeoPointSeriesNominations);
        return pointStats;
    }

    getResult(run: RodeoEventRun, useScoreValue: boolean, isAggregateOrAverage: boolean) {
        let numericValue = 0;
        if (isAggregateOrAverage)
            numericValue = useScoreValue ? run.totalScore : run.totalTime;
        else
            numericValue = useScoreValue ? run.score : run.time;

        return numericValue;
    }

    splitPointsForTies(pointStats: PointStatResult[]) {
        const groupedPointStats = groupBy(pointStats, ({ result, leaderCategory, pointAssignmentId }) => `${result}_${leaderCategory}_${pointAssignmentId}`)
        let runIdsWithTies = {};
        Object.keys(groupedPointStats)
            .filter(key => groupedPointStats[key].length > 1)
            .forEach(key => {
                const tiedPointStats = groupedPointStats[key];
                const totalPoints = tiedPointStats.reduce((acc, ps) => acc + ps.points, 0);
                const pointsAwarded = totalPoints / tiedPointStats.length;
                tiedPointStats.forEach(ps => {
                    runIdsWithTies[getPointStatKey(ps)] = pointsAwarded;
                })
            });
        if (Object.keys(runIdsWithTies).length === 0) return pointStats;

        return pointStats.map(ps => {
            const tiedPoints = runIdsWithTies[getPointStatKey(ps)];
            if (tiedPoints)
                return {
                    ...ps,
                    points: tiedPoints
                }
            return ps;
        });
    }

    /**
     * update places when ties exists
     * input:
     *      Place | Points
     *         1      40
     *         2      20
     *         3      20
     *         4      15
     * output: 
     *      Place | Points
     *         1      40
     *        *2*     20
     *        *2*     20
     *         3      15
     * @param pointStats 
     * @returns pointStats
     */
    handleTiePlaces(pointStats: PointStats[]): PointStats[] {
        const groupedPointStats = groupBy(pointStats, ({ leaderCategory, pointAssignmentId }) => `${leaderCategory}-${pointAssignmentId}`);
        let result = [];
        Object.keys(groupedPointStats).forEach(key => {
            const payOutConfigPointStats = groupedPointStats[key];
            let place = 0;
            let tiedPlace = 0;
            const placeSortedPointStats = payOutConfigPointStats.map((p, i) => {
                const previousLeader = payOutConfigPointStats[i - 1];
                place = place + 1;
                if (previousLeader) {
                    if (p.points !== 0) {
                        const previousPoints = previousLeader.points;
                        if (previousPoints === p.points)
                            return {
                                ...p,
                                place: tiedPlace
                            }
                    } else {
                        // Probably working with a contestant two payout
                        const previousPoints = previousLeader.rider2Points;
                        if (previousPoints === p.rider2Points)
                            return {
                                ...p,
                                place: tiedPlace
                            }
                    }
                }
                tiedPlace = place;
                return {
                    ...p,
                    place: place,
                }
            })
            result = result.concat(placeSortedPointStats);
        });
        return result;
    }

    private getPayoutEntryCount(payout: PayoutConfig, rodeo: Rodeo) {
        return payout.sources.reduce(
            (acc, source) => acc + LeaderGenerator.instance().getTotalCount(rodeo, payout, source, true, true),
            0);
    }

    private getTrackedPointAssigments(firstPointAssigmentsInEachSeries: PointAssignment[], entryCount: number, payout: PayoutConfig) {
        return firstPointAssigmentsInEachSeries
            .map((pa): PointAssigmentTracked => {
                const correspondingPointSeries = payout.pointSeries.find(ps => ps.id === pa.pointSeriesId);
                return {
                    ...pa,
                    places: PointsCalc.CalculatePlaces(entryCount, pa.entryScaleMultiple, pa.placesAwardedPerEntryScale),
                    currentPoints: 0,
                    membersOnly: correspondingPointSeries.membersOnly,
                    entryCount: entryCount,
                    enableNominations: correspondingPointSeries.enableNominations,
                };
            });
    }

    private getFirstPointAssigmentInEachSeries(payout: PayoutConfig, entryCost: number, isSidePot: boolean) {
        const firstPointAssigmentsInEachSeries: Array<PointAssignment> = [];
        payout.pointSeries.forEach(ps => {
            const matchingPointAssigment = ps.pointAssignments.find(pa => PointsUtilities.isMatchingPointAssigment(pa, entryCost, isSidePot, (payout.isAggregate || payout.isAverage)));
            if (matchingPointAssigment)
                firstPointAssigmentsInEachSeries.push(matchingPointAssigment);
        });
        return firstPointAssigmentsInEachSeries;
    }

    private checkIfIsSidepot(payout: PayoutConfig) {
        return payout.sources[0].sidepotId ? true : false;
    }
}

const getPointStatKey = (ps: PointStatResult) => {
    return `${ps.rodeoEventRunId}-${ps.leaderCategory}-${ps.pointAssignmentId}`;
}



// WEBPACK FOOTER //
// ./src/services/points/points.domian.service.ts